import React, { useState } from "react";
import { Button, Tag, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { actions as experienceUserActions } from "/src/views/ExperienceUsers/redux";
import { formatJsonApiData } from "mr_react_framework";
import ResponseAttachments from "/src/views/Segments/ResponseAttachments/ResponseAttachments";
import { showConfirmModal, UploadFilesButton } from "./Segment/UIHelper";
import { useTranslate } from "/src/lib/MrTranslate/MrTranslate";
import { getFromLS, getUniqueId } from "/src/lib/utils/helperMethods";
import { activeAdjustedExperienceSelector, activeExperienceGradingDisabledSelector } from "/src/views/Experiences/selector";
import { usePollingEffect } from "/src/views/Segments/InteractiveHelpers";
import { currentUserSelector } from "/src/views/Auth/Login/selector";
import { enabledFeaturesSelector } from "/src/views/Auth/Login/selector";

export const useAutoMappingProgress = (experienceUser, shouldPoll, onSuccessCallback) => {
  console.log("useAutoMappingProgress ===>", {experienceUser, shouldPoll});
  const dispatch = useDispatch();
  const checkAutomappingProgress = (clearIntervalCallback) => {
    dispatch(experienceUserActions.show({ 
      id: experienceUser.id,
    }, {
      successCallback: (response) => {
        const updatedExperienceUser = formatJsonApiData(response.data.experience_user);        
        const updatedAutoMappingProgress = updatedExperienceUser.custom_fields?.["auto_mapping_progress"];
                
        if(onSuccessCallback) {
          onSuccessCallback(updatedExperienceUser)
        }
        const progressCompleted = ["completed", "failed"].includes(updatedAutoMappingProgress?.status);
        if (progressCompleted) {
          clearIntervalCallback();
          // message[updatedAutoMappingProgress.status === "completed" ? "success" : "error"](`${updatedAutoMappingProgress["message"]}. ${updatedAutoMappingProgress.status === "failed" ? "Please try again." : ""}`);
        }
      }
    }));
  };

  usePollingEffect(shouldPoll, checkAutomappingProgress, 10000);
};

const ResponseAttachmentsModal = ({ count = null, item, type = "", shape = "", size = "", buttonText = "", icon = null, disabled = false, canUpload = false, isInsideExamView = false }) => {
  console.log("ViewUploadsModal props ==>", { count, item, type, shape, size, icon })
  const [isModalOpen, setIsModalOpen] = useState(false);
  const mrIntl = useTranslate()
  const dispatch = useDispatch();
  
  const originalUserRole = getFromLS("originalUserRole")
  const experience = useSelector(activeAdjustedExperienceSelector());
  const activeExperienceGradingDisabled = useSelector(activeExperienceGradingDisabledSelector());
  const enabledFeatures = useSelector(enabledFeaturesSelector())
  const currentUser = useSelector(currentUserSelector());

  const experienceSettings = experience.settings || {};
  const allowManualMapping = enabledFeatures.allow_manual_mapping;
  
  // For join views, item will be undefined, so we need to use the experience and currentUser to create a new experienceUser object
  const experienceUser = item ?? { experience_id: experience.id, user_id: currentUser.id };
  const customFields = experienceUser.custom_fields || {};
  const autoMappingProgress = customFields["auto_mapping_progress"];
  const pdfConversionProgress = customFields["pdf_conversion_progress"]; 
  const isPaperMode = experienceSettings.mode === "paper";
  const isExperienceClosed = experience.status === "closed";
  const isQuestionMappingEnabled = !isInsideExamView && canUpload && (isPaperMode || allowManualMapping);

  const shouldPoll =
    isQuestionMappingEnabled &&
    isModalOpen &&
    (autoMappingProgress || pdfConversionProgress) &&
    ((autoMappingProgress &&
      !["failed", "completed"].includes(autoMappingProgress["status"])) ||
      (pdfConversionProgress &&
        !["failed", "completed"].includes(pdfConversionProgress["status"])));
  
  useAutoMappingProgress(experienceUser, shouldPoll);

  const uploadButtonStyle = {
    width: "100%", 
    height: "100%",
    textAlign: "center"
  }

  const submitAttachments = (
    attachments_json,
    options,
  ) => {
    const { submitAttachmentsStartCallback, submitAttachmentsCompletedCallback } = options;
    console.log(
      "submit attachments ==>",
      attachments_json,
      submitAttachmentsCompletedCallback
    );
    let submitData = { 
      user_id: experienceUser.user_id,
      experience_id : experienceUser.experience_id,
      uploads_allowed: false, 
      attachments_count: attachments_json?.attachments?.length ?? 0,
      attachments_json: attachments_json,
    }

    submitAttachmentsStartCallback();
    dispatch(
      experienceUserActions.submitAttachments(
        submitData,
        {
          success: {
            showMessage: true,
            message: "Files submitted successfully.",
          },
          successCallback: (response) => {
            const finalExperienceUser = formatJsonApiData(
              response.data.experience_user
            );
            const updatedExperienceUser = {
              ...experienceUser,
              ...finalExperienceUser,
            };
            console.log(
              "response after upload =>",
              experienceUser,
              response,
              finalExperienceUser,
              updatedExperienceUser
            );
            dispatch(
              experienceUserActions.showSuccess(
                { data: { experience_user: updatedExperienceUser } },
                { success: { showMessage: false } }
              )
            );
            // setTriggerCheckProgressInterval(getUniqueId())
            submitAttachmentsCompletedCallback(response);
            if (!options.doNotCloseModal) {
              setIsModalOpen(false);
            }
          },
          errorCallback: (error) => {
            submitAttachmentsCompletedCallback(error);
          },
        }
      )
    );
  };

  const confirmModalTitle = 
    isInsideExamView 
      ? mrIntl("ResponseAttachmentsModal.important_final_submission_confirmation")
      : mrIntl("ViewUploadsModal.submitting_attachments");

  const confirmModalContent = 
    isInsideExamView 
      ? (
      <>
        {mrIntl("ResponseAttachmentsModal.are_you_sure_you_want_to_submit_these_files")} <br/>
        {mrIntl("CommonText.please_note")}
          <ul>
            <li>
              {mrIntl("ResponseAttachmentsModal.these_files_will_be_counted_as_official_submissions_for_your")}
            </li>
            <li>{mrIntl("ResponseAttachmentsModal.once_submitted_these_files_can_not_be")} <span className="c-red">{mrIntl("ResponseAttachmentsModal.deleted_resubmitted_or_modified")}</span> </li>
          </ul>
          {mrIntl("CommonText.click_confirm_to_proceed_with_submission_or_cancel_to_review")}
        </>
      ) 
      : mrIntl("ViewUploadsModal.are_you_sure_you_want_to_submit_these_files_these");

  const handleSubmitAttachments = (
    attachmentsJson,
    options
  ) => {
    if (!options.doNotShowConfirmModal) {
      showConfirmModal({
        centered: true,
        title: confirmModalTitle,
        content: confirmModalContent,
        onOk: () => {
          submitAttachments(
            attachmentsJson,
            options
          );
        },
        onCancel: () => {},
        mrIntl: mrIntl,
        okText: mrIntl("ResponseAttachmentsModal.confirm"),
        cancelText:mrIntl("CommonText.cancel"),
        width: 600
      });
    } else {
      submitAttachments(
        attachmentsJson,
        options
      );
    }
  };

  const handleCloseModal = (changed) => {
    if (changed) {
      showConfirmModal({
        centered: true,
        title: mrIntl("ViewUploadsModal.cancel_submission"),
        content:
          mrIntl("ViewUploadsModal.some_attachments_are_not_saved_are_you_sure_you_wish"),
        onOk: () => {
          setIsModalOpen(false);
        },
        mrIntl: mrIntl
      });
    } else {
      setIsModalOpen(false);
    }
  };

const responseAttachmentsDisableUploader = 
  experienceUser.marked || activeExperienceGradingDisabled
const responseAttachmentsDisabledUploaderTooltip = 
  activeExperienceGradingDisabled 
    ? null
    : mrIntl("ViewUploadsModal.please_unpublish_the_result_to_upload");

  const responseAttachments = (
    <ResponseAttachments
      answerMode={canUpload} 
      listType={isQuestionMappingEnabled ? "text" : null}
      previewType={"modal"}
      maxAttachments={100}
      recordId={experienceUser.experience_id}
      userId={experienceUser.user_id} // sending userId when role is teacher else null
      modalVisible={isModalOpen}
      attachments_json={experienceUser.attachments_json || {}}
      deleteNotAllowedMessage={!(originalUserRole === "superadmin" || originalUserRole === "support")}
      onSubmit={handleSubmitAttachments}
      closeModal={handleCloseModal}
      disableUploader={responseAttachmentsDisableUploader}
      disabledUploaderTooltip={responseAttachmentsDisabledUploaderTooltip}
      isQuestionMappingEnabled={isQuestionMappingEnabled}
      experienceSettings={experienceSettings}
    />
  );

  let uploadFilesBtnTooltip = null, uploadFilesBtnDisabled = false;
  if (isExperienceClosed) {
    uploadFilesBtnDisabled = true;
    uploadFilesBtnTooltip = mrIntl("CommonText.test_has_been_closed_for_submissions");
  } 

  return (
    <React.Fragment>
      {isModalOpen ? responseAttachments : null}

      {(isInsideExamView && canUpload) ? (
        <UploadFilesButton
          disabled={uploadFilesBtnDisabled}
          buttonText={mrIntl("ExperienceList.upload")}
          shape="round"
          type={'primary'}
          clicked={() => {
            setIsModalOpen(true);
          }}
          tooltipTitle={uploadFilesBtnTooltip}
        />
      ) : (
        <Button
          type={type}
          shape={shape}
          size={size}
          icon={icon}
          style={uploadButtonStyle}
          disabled={disabled}
          onClick={() => setIsModalOpen(true)}
        >
          <Tooltip title="Total uploads" placement="bottom">
            {count && <Tag>{count}</Tag>}
          </Tooltip>
          {buttonText}
        </Button>
      )}
    </React.Fragment>
  );
};

export default ResponseAttachmentsModal;
